import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

export function StoreProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], subtotal: 0, count: 0 });
  const [wishlist, setWishlist] = useState([]);
  const [settings, setSettings] = useState(null);

  const loadPublic = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/public");
      setSettings(data);
    } catch {
      /* non-blocking */
    }
  }, []);

  const refreshCart = useCallback(async () => {
    if (!user) {
      setCart({ items: [], subtotal: 0, count: 0 });
      return;
    }
    try {
      const { data } = await api.get("/cart");
      setCart(data);
    } catch {
      /* ignore */
    }
  }, [user]);

  const refreshWishlist = useCallback(async () => {
    if (!user) {
      setWishlist([]);
      return;
    }
    try {
      const { data } = await api.get("/wishlist");
      setWishlist(data);
    } catch {
      /* ignore */
    }
  }, [user]);

  useEffect(() => {
    loadPublic();
  }, [loadPublic]);

  useEffect(() => {
    refreshCart();
    refreshWishlist();
  }, [refreshCart, refreshWishlist]);

  const requireLogin = () => {
    toast.error("Please sign in to continue.");
    return false;
  };

  const addToCart = async (productId, qty = 1) => {
    if (!user) return requireLogin();
    try {
      const { data } = await api.post("/cart", { product_id: productId, qty });
      setCart(data);
      toast.success("Added to cart");
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    }
  };

  const updateCartItem = async (itemId, qty) => {
    try {
      const { data } = await api.put(`/cart/${itemId}`, { qty });
      setCart(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeCartItem = async (itemId) => {
    try {
      const { data } = await api.delete(`/cart/${itemId}`);
      setCart(data);
      toast.success("Removed from cart");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const isWished = (productId) => wishlist.some((p) => p.id === productId);

  const toggleWishlist = async (productId) => {
    if (!user) return requireLogin();
    try {
      if (isWished(productId)) {
        await api.delete(`/wishlist/${productId}`);
        toast.success("Removed from wishlist");
      } else {
        await api.post(`/wishlist/${productId}`);
        toast.success("Added to wishlist");
      }
      refreshWishlist();
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    }
  };

  return (
    <StoreContext.Provider
      value={{
        cart, wishlist, settings,
        refreshCart, refreshWishlist, loadPublic,
        addToCart, updateCartItem, removeCartItem,
        isWished, toggleWishlist,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
}
